// src/lib/healthCheck.ts
import * as Sentry from '@sentry/react';
import { API_CONFIG } from './config';
import logger from './logger';

interface ServiceStatus {
  status: 'ok' | 'degraded' | 'error';
  latencyMs?: number;
  error?: string;
}

export interface HealthStatus {
  status: 'ok' | 'degraded' | 'error';
  timestamp: string;
  services: {
    api: ServiceStatus;
    sentry: ServiceStatus;
  };
}

/**
 * Check API backend connectivity by pinging the health endpoint.
 */
async function checkApiHealth(): Promise<ServiceStatus> {
  const start = performance.now();
  try {
    const response = await fetch(`${API_CONFIG.baseURL}/health/`, {
      method: 'GET',
      headers: { Accept: 'application/json' },
      signal: AbortSignal.timeout(10000),
    });
    const latencyMs = Math.round(performance.now() - start);

    if (response.ok) {
      return { status: 'ok', latencyMs };
    }
    return {
      status: 'degraded',
      latencyMs,
      error: `HTTP ${response.status}`,
    };
  } catch (error) {
    const latencyMs = Math.round(performance.now() - start);
    return {
      status: 'error',
      latencyMs,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

/**
 * Check Sentry connectivity by verifying the client is active.
 */
function checkSentryHealth(): ServiceStatus {
  try {
    const client = Sentry.getClient();
    if (client) {
      return { status: 'ok' };
    }
    return { status: 'degraded', error: 'Sentry client not initialized' };
  } catch (error) {
    return {
      status: 'error',
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

/**
 * Run a comprehensive health check of the frontend application.
 * Checks API backend connectivity and Sentry integration status.
 *
 * Returns a structured health status object suitable for monitoring tools.
 */
export async function checkHealth(): Promise<HealthStatus> {
  const [apiStatus] = await Promise.all([checkApiHealth()]);
  const sentryStatus = checkSentryHealth();

  const services = { api: apiStatus, sentry: sentryStatus };

  // Overall status: error if any service is in error, degraded if any degraded
  let overallStatus: HealthStatus['status'] = 'ok';
  const statuses = Object.values(services);
  if (statuses.some((s) => s.status === 'error')) {
    overallStatus = 'error';
  } else if (statuses.some((s) => s.status === 'degraded')) {
    overallStatus = 'degraded';
  }

  const result: HealthStatus = {
    status: overallStatus,
    timestamp: new Date().toISOString(),
    services,
  };

  logger.log('Health check result:', result);
  return result;
}

/**
 * Run a one-time health check at application startup.
 * Logs the result and reports degraded status to Sentry.
 */
export function runStartupHealthCheck(): void {
  checkHealth()
    .then((status) => {
      if (status.status !== 'ok') {
        logger.warn('Startup health check: services degraded', {
          status: status.status,
          api: status.services.api.status,
          sentry: status.services.sentry.status,
        });
        Sentry.setContext('health_check', {
          overall: status.status,
          api_status: status.services.api.status,
          api_latency_ms: status.services.api.latencyMs,
          sentry_status: status.services.sentry.status,
        });
      }
    })
    .catch((err) => {
      logger.error('Startup health check failed', err);
    });
}
